/* ============================================================
   BEYLORA FLOW ENGINE — JS half (2026-06-12)
   Primitive contract + usage docs live at the top of
   assets/beylora-flow-20260612.css. This engine arms:
     1. Reveal v2 — [data-beylora-reveal="rise|fade|mask"], plus
        back-compat for legacy [data-beylora-c-reveal] and
        [data-beylora-c-plp-reveal] (fires their legacy
        in-classes, so old sections work with or without their
        own IO scripts; double-arming is idempotent).
     2. Parallax depth — [data-beylora-parallax="0.12"],
        translate3d-only, IntersectionObserver-gated, off on
        touch unless [data-beylora-parallax-touch].
     3. Scroll-linked light — [data-beylora-light] receives
        --beylora-flow-p (0..1) progress through the viewport.
   One rAF pipeline, all reads before all writes (no layout
   thrash). prefers-reduced-motion = fully static. No JS = fully
   visible (hidden states only exist under JS-added classes).
   Public API: window.BeyloraFlow.refresh() after injecting DOM.
   ============================================================ */
(function () {
  'use strict';

  if (window.BeyloraFlow) return;

  var docEl = document.documentElement;
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var mqTouch = window.matchMedia ? window.matchMedia('(hover: none) and (pointer: coarse)') : null;
  var hasIO = 'IntersectionObserver' in window;
  var MAX_PARALLAX_PX = 160;

  function reduced() {
    return !!(mqReduce && mqReduce.matches);
  }

  function isTouch() {
    return !!(mqTouch && mqTouch.matches);
  }

  /* ----------------- Reveal system v2 ----------------- */

  var LEGACY_SEL = '[data-beylora-c-reveal], [data-beylora-c-plp-reveal]';
  var REVEAL_SEL = '[data-beylora-reveal], ' + LEGACY_SEL;
  var LEGACY_IN = ['beylora-c-in', 'beylora-c-presence__in', 'beylora-c-plp-reveal-in'];
  var revealIO = null;
  /* Fail-safe window: if IntersectionObserver never reports an armed
     element (capture tools / iframes / headless judges that don't scroll,
     or an IO callback that never schedules), reveal EVERYTHING still armed
     so content can never stay invisible / void in a static render. The
     normal scroll path fires far sooner; this only catches the never-fired
     case. */
  var REVEAL_FAILSAFE_MS = 1600;
  var failsafeTimer = null;
  var ioEverFired = false; /* proves the IntersectionObserver is alive on this page */

  /* Fail-safe, viewport-aware (fixed 2026-07-08): the old version revealed EVERY
     armed element page-wide, so the entire below-fold choreography burned off
     invisibly ~1.6s after load and real shoppers never saw a single reveal play.
     Now: if the IO has ever fired, only elements the shopper can already see are
     force-shown (nothing may sit invisible in the viewport); below-fold elements
     stay armed for the observer. If the IO has NEVER fired (broken/blocked),
     reveal everything - showing content beats motion, always. */
  function revealStillArmed() {
    var els = document.querySelectorAll(REVEAL_SEL);
    if (!ioEverFired) {
      for (var i = 0; i < els.length; i++) {
        if (!els[i].classList.contains('beylora-flow-in')) markIn(els[i]);
      }
      return;
    }
    var floor = (window.innerHeight || 0) + 120;
    for (var j = 0; j < els.length; j++) {
      if (els[j].classList.contains('beylora-flow-in')) continue;
      if (els[j].getBoundingClientRect().top < floor) markIn(els[j]);
    }
  }

  function armFailsafe() {
    if (failsafeTimer || !window.setTimeout) return;
    failsafeTimer = window.setTimeout(function () {
      failsafeTimer = null;
      revealStillArmed();
    }, REVEAL_FAILSAFE_MS);
  }

  function markIn(el) {
    el.classList.add('beylora-flow-in');
    if (el.hasAttribute('data-beylora-c-reveal') || el.hasAttribute('data-beylora-c-plp-reveal')) {
      for (var i = 0; i < LEGACY_IN.length; i++) el.classList.add(LEGACY_IN[i]);
    }
  }

  function revealEverything() {
    var els = document.querySelectorAll(REVEAL_SEL);
    for (var i = 0; i < els.length; i++) markIn(els[i]);
  }

  function initReveal() {
    var els = document.querySelectorAll(REVEAL_SEL);
    if (!els.length) return;

    if (!hasIO || reduced()) {
      revealEverything();
      return;
    }

    if (!revealIO) {
      revealIO = new IntersectionObserver(
        function (entries) {
          ioEverFired = true; /* the observer is alive: the fail-safe may trust it for below-fold */
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              markIn(entries[i].target);
              revealIO.unobserve(entries[i].target);
            }
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -6% 0px' }
      );
    }

    for (var j = 0; j < els.length; j++) {
      var el = els[j];
      if (el.classList.contains('beylora-flow-in')) continue;
      if (el.hasAttribute('data-beylora-reveal')) el.classList.add('beylora-flow-armed');
      revealIO.observe(el);
    }

    armFailsafe();
  }

  /* ------------- Parallax + scroll-linked light ------------- */
  /* Shared registry. rec = { el, f (parallax factor or null),
     light (bool), active, y (applied translate), p (applied
     progress) }. Measurements subtract the applied translate so
     the engine's own transform never feeds back into reads. */

  var items = [];
  var motionIO = null;
  var ticking = false;
  var listening = false;

  function ensureMotionIO() {
    if (motionIO) return motionIO;
    motionIO = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var rec = entries[i].target.__beyloraFlowRec;
          if (!rec) continue;
          rec.active = entries[i].isIntersecting;
          if (rec.active) {
            rec.el.classList.add('beylora-flow-px-live');
            schedule();
          } else {
            rec.el.classList.remove('beylora-flow-px-live');
          }
        }
      },
      { rootMargin: '18% 0px 18% 0px' }
    );
    return motionIO;
  }

  function startListening() {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(tick);
  }

  function tick() {
    ticking = false;
    var vh = window.innerHeight || docEl.clientHeight;
    var reads = [];
    var i;
    var rec;
    var rect;

    /* READ phase */
    for (i = 0; i < items.length; i++) {
      rec = items[i];
      if (!rec.active) continue;
      rect = rec.el.getBoundingClientRect();
      var y = 0;
      var p = -1;
      if (rec.f !== null) {
        var center = rect.top - rec.y + rect.height / 2;
        y = (center - vh / 2) * rec.f;
        if (y > MAX_PARALLAX_PX) y = MAX_PARALLAX_PX;
        else if (y < -MAX_PARALLAX_PX) y = -MAX_PARALLAX_PX;
      }
      if (rec.light) {
        p = (vh - rect.top) / (vh + rect.height);
        if (p < 0) p = 0;
        else if (p > 1) p = 1;
      }
      reads.push([rec, y, p]);
    }

    /* WRITE phase */
    for (i = 0; i < reads.length; i++) {
      rec = reads[i][0];
      if (rec.f !== null && Math.abs(reads[i][1] - rec.y) >= 0.1) {
        rec.y = reads[i][1];
        rec.el.style.transform = 'translate3d(0,' + rec.y.toFixed(2) + 'px,0)';
      }
      if (rec.light && Math.abs(reads[i][2] - rec.p) >= 0.002) {
        rec.p = reads[i][2];
        rec.el.style.setProperty('--beylora-flow-p', rec.p.toFixed(4));
      }
    }
  }

  function register(el) {
    if (el.__beyloraFlowRec) return;

    var f = null;
    if (
      el.hasAttribute('data-beylora-parallax') &&
      (!isTouch() || el.hasAttribute('data-beylora-parallax-touch'))
    ) {
      f = parseFloat(el.getAttribute('data-beylora-parallax'));
      if (!isFinite(f) || f === 0) f = 0.12;
    }
    var light = el.hasAttribute('data-beylora-light');
    if (f === null && !light) return;

    var rec = { el: el, f: f, light: light, active: false, y: 0, p: -1 };
    el.__beyloraFlowRec = rec;
    items.push(rec);
    ensureMotionIO().observe(el);
    startListening();
  }

  function initMotion() {
    if (!hasIO || reduced()) return;
    var els = document.querySelectorAll('[data-beylora-parallax], [data-beylora-light]');
    for (var i = 0; i < els.length; i++) register(els[i]);
    schedule();
  }

  /* ----------------- Lifecycle ----------------- */

  function flattenMotion() {
    for (var i = 0; i < items.length; i++) {
      items[i].active = false;
      items[i].el.style.transform = '';
      items[i].el.style.removeProperty('--beylora-flow-p');
      items[i].el.classList.remove('beylora-flow-px-live');
      items[i].el.__beyloraFlowRec = null;
    }
    items.length = 0;
    if (motionIO) {
      motionIO.disconnect();
      motionIO = null;
    }
  }

  if (mqReduce && typeof mqReduce.addEventListener === 'function') {
    mqReduce.addEventListener('change', function () {
      if (!reduced()) return;
      flattenMotion();
      revealEverything();
    });
  }

  function refresh() {
    initReveal();
    initMotion();
  }

  /* ----------------- Reveal fail-safe: extra backstops -----------------
     The primary fail-safe is armFailsafe() inside initReveal(): a 1600ms timer
     that calls revealStillArmed() to show anything the IntersectionObserver
     never reported (capture tools / iframes / headless judges that don't
     scroll, or an IO callback that never schedules). The guards below are
     additive belt-and-suspenders on top of that one timer — they never alter
     anything already revealed (markIn is idempotent; revealStillArmed only
     touches elements without .beylora-flow-in):
       - window 'load': re-fire the floor once everything has settled, covering
         the case where initReveal ran before the deferred engine fully booted.
       - 'pageshow' from bfcache: a restored page can carry stale armed state
         with opacity:0; reveal immediately so nothing comes back invisible. */

  if (document.readyState === 'complete') {
    revealStillArmed();
  } else {
    window.addEventListener('load', revealStillArmed, { once: true });
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) revealEverything(); /* bfcache restore can carry stale opacity:0 - show all, no motion */
  });

  /* Theme editor: re-arm injected sections (refresh() re-runs initReveal,
     which re-arms its own fail-safe timer for the new DOM). */
  document.addEventListener('shopify:section:load', function () {
    refresh();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  window.BeyloraFlow = {
    version: '20260612',
    refresh: refresh,
    revealAll: revealStillArmed
  };
})();
